import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Shared service for managing search grid state across components
 * that are not in a direct parent-child relationship.
 *
 * **Use Case:**
 * When you have a search grid component rendered in one part of the application
 * (e.g., through a router-outlet) and need to control it from another component
 * (e.g., a navbar or toolbar) that doesn't have a direct parent-child relationship.
 *
 * **Example Usage:**
 *
 * 1. **In the Search Grid Component:**
 * ```typescript
 * export class MySearchComponent implements OnInit, OnDestroy {
 *   constructor(private gridStateService: CdwSearchGridStateService) {}
 *
 *   ngOnInit() {
 *     // Listen for toggle edit mode requests
 *     this.gridStateService.toggleEditMode$
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(() => {
 *         this.searchGrid.toggleEditMode();
 *       });
 *   }
 *
 *   onEditModeChanged(isEditMode: boolean) {
 *     this.gridStateService.setEditMode(isEditMode);
 *   }
 *
 *   onSelectedIdsChanged(selectedIds: (string | number)[]) {
 *     this.gridStateService.setSelectedCount(selectedIds.length);
 *   }
 * }
 * ```
 *
 * 2. **In the Navbar/Toolbar Component:**
 * ```typescript
 * export class NavbarComponent implements OnInit {
 *   constructor(private gridStateService: CdwSearchGridStateService) {}
 *
 *   ngOnInit() {
 *     // Subscribe to edit mode state
 *     this.gridStateService.isEditMode$
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(isEditMode => {
 *         this.updateButtonState(isEditMode);
 *       });
 *
 *     // Subscribe to selected count
 *     this.gridStateService.selectedCount$
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(count => {
 *         this.enableEditButton = count > 0;
 *       });
 *   }
 *
 *   onEditModeButtonClick() {
 *     this.gridStateService.toggleEditMode();
 *   }
 * }
 * ```
 *
 * **Note:** If you need multiple independent grid instances with separate state,
 * provide this service at the component level instead of root:
 * ```typescript
 * @Component({
 *   providers: [CdwSearchGridStateService]
 * })
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class CdwSearchGridStateService {
  // Edit mode state
  private isEditModeSubject = new BehaviorSubject<boolean>(false);
  /**
   * Observable that emits the current edit mode state (true/false)
   */
  public isEditMode$: Observable<boolean> =
    this.isEditModeSubject.asObservable();

  // Selected rows count
  private selectedCountSubject = new BehaviorSubject<number>(0);
  /**
   * Observable that emits the count of currently selected rows
   */
  public selectedCount$: Observable<number> =
    this.selectedCountSubject.asObservable();

  // Total item count (all rows matching current query, not just displayed)
  private itemTotalSubject = new BehaviorSubject<number>(0);
  /**
   * Observable that emits the total item count for the current query
   */
  public itemTotal$: Observable<number> =
    this.itemTotalSubject.asObservable();

  // Selected row IDs
  private selectedIdsSubject = new BehaviorSubject<(string | number)[]>([]);
  /**
   * Observable that emits the array of currently selected row IDs
   */
  public selectedIds$: Observable<(string | number)[]> =
    this.selectedIdsSubject.asObservable();

  // Refresh specific rows by ID (force refresh from source — e.g., Google)
  private refreshRowsSubject = new Subject<(string | number)[]>();
  /**
   * Observable that emits row IDs that should be force-refreshed from the source.
   * Subscribe to this in your search grid component to trigger targeted row refresh.
   */
  public refreshRows$: Observable<(string | number)[]> =
    this.refreshRowsSubject.asObservable();

  // Update rows from cache — used after bulk action completion when cache is already fresh
  private updateRowsSubject = new Subject<(string | number)[]>();
  /**
   * Observable that emits row IDs that should be re-fetched from cache and updated in place.
   * Unlike refreshRows$, this does NOT trigger a force refresh from the source.
   */
  public updateRows$: Observable<(string | number)[]> =
    this.updateRowsSubject.asObservable();

  // Toggle edit mode command
  private toggleEditModeSubject = new Subject<void>();
  /**
   * Observable that emits when edit mode should be toggled.
   * Subscribe to this in your search grid component to trigger the toggle action.
   */
  public toggleEditMode$: Observable<void> =
    this.toggleEditModeSubject.asObservable();

  /**
   * Update the edit mode state
   * @param isEditMode - true if entering edit mode, false if exiting
   */
  setEditMode(isEditMode: boolean): void {
    this.isEditModeSubject.next(isEditMode);
  }

  /**
   * Update the total item count for the current query
   * @param total - total number of items matching the current filters
   */
  setItemTotal(total: number): void {
    this.itemTotalSubject.next(total);
  }

  /**
   * Get the current total item count synchronously
   * @returns total number of items matching the current filters
   */
  getItemTotal(): number {
    return this.itemTotalSubject.value;
  }

  /**
   * Update the count of selected rows
   * @param count - number of currently selected rows
   */
  setSelectedCount(count: number): void {
    this.selectedCountSubject.next(count);
  }

  /**
   * Update the array of selected row IDs
   * @param ids - array of currently selected row IDs
   */
  setSelectedIds(ids: (string | number)[]): void {
    this.selectedIdsSubject.next(ids);
  }

  /**
   * Trigger a toggle edit mode action.
   * This emits an event that the search grid component should listen to.
   */
  toggleEditMode(): void {
    this.toggleEditModeSubject.next();
  }

  /**
   * Request a targeted force refresh of specific rows by their IDs.
   * The consuming component is responsible for fetching fresh data from source and updating the grid.
   */
  refreshRows(ids: (string | number)[]): void {
    this.refreshRowsSubject.next(ids);
  }

  /**
   * Request a cache-based update of specific rows by their IDs.
   * Used after bulk actions complete — the cache already has fresh data, no force refresh needed.
   */
  updateRows(ids: (string | number)[]): void {
    this.updateRowsSubject.next(ids);
  }

  /**
   * Get the current edit mode state synchronously
   * @returns true if currently in edit mode, false otherwise
   */
  getEditMode(): boolean {
    return this.isEditModeSubject.value;
  }

  /**
   * Get the current selected count synchronously
   * @returns the number of currently selected rows
   */
  getSelectedCount(): number {
    return this.selectedCountSubject.value;
  }

  /**
   * Get the current selected row IDs synchronously
   * @returns array of currently selected row IDs
   */
  getSelectedIds(): (string | number)[] {
    return this.selectedIdsSubject.value;
  }

  // Updating rows tracking — rows with active bulk actions in progress.
  // BehaviorSubject so the SSRM grid's [updatingIds$] @Input can subscribe and
  // surgically redraw affected visible rows via the augmentation layer.
  // Emits a NEW Set on each mutation so RxJS reference-distinct comparisons
  // (e.g. distinctUntilChanged) detect the change.
  private readonly updatingIdsSubject = new BehaviorSubject<
    Set<string | number>
  >(new Set());

  /**
   * Observable that emits the current set of row IDs currently being updated.
   * The SSRM grid consumes this via `[updatingIds$]` to drive the spinner +
   * stale-row CSS on visible rows. Legacy clientSide consumers can ignore it.
   */
  public updatingIds$: Observable<Set<string | number>> =
    this.updatingIdsSubject.asObservable();

  /**
   * Mark row IDs as currently updating (bulk action in progress).
   * These rows will be greyed out, click-disabled, and show a spinner.
   */
  markAsUpdating(ids: (string | number)[]): void {
    if (ids.length === 0) return;
    const next = new Set(this.updatingIdsSubject.value);
    for (const id of ids) {
      next.add(id);
    }
    this.updatingIdsSubject.next(next);
  }

  /**
   * Clear the updating state for specific row IDs (e.g., on job completion or error)
   */
  clearUpdating(ids: (string | number)[]): void {
    if (ids.length === 0) return;
    const next = new Set(this.updatingIdsSubject.value);
    let mutated = false;
    for (const id of ids) {
      if (next.delete(id)) mutated = true;
    }
    if (mutated) {
      this.updatingIdsSubject.next(next);
    }
  }

  /**
   * Clear all updating row IDs
   */
  clearAllUpdating(): void {
    if (this.updatingIdsSubject.value.size === 0) return;
    this.updatingIdsSubject.next(new Set());
  }

  /**
   * Check if a specific row ID is currently updating
   */
  isUpdating(id: string | number): boolean {
    return this.updatingIdsSubject.value.has(id);
  }

  /**
   * Get the current set of updating row IDs synchronously
   */
  getUpdatingIds(): Set<string | number> {
    return this.updatingIdsSubject.value;
  }
}
